





















import {
  ref,
  computed,
  useContext,
  useRoute,
  defineComponent,
  useFetch,
  onMounted,
} from '@nuxtjs/composition-api';
import { merge } from 'lodash-es';
import { useCache, CacheTagPrefix } from '@vue-storefront/cache';
import { SfBreadcrumbs, SfLoader } from '@storefront-ui/vue';
import { getBreadcrumbs } from '~/modules/catalog/product/getters/productGetters';
import { useProduct } from '~/modules/catalog/product/composables/useProduct';
import { getMetaInfo } from '~/helpers/getMetaInfo';
import { usePageStore } from '~/stores/page';
import { ProductTypeEnum } from '~/modules/catalog/product/enums/ProductTypeEnum';
import { useWishlist, useApi } from '~/composables';
/* import useComparelist from '~/modules/comparelist/composables/useComparelist'; */
import LoadWhenVisible from '~/components/utils/LoadWhenVisible.vue';
import type { Product } from '~/modules/catalog/product/types';
import type { ProductDetailsQuery } from '~/modules/GraphQL/types';
import ProductSkeleton from '~/modules/catalog/product/components/ProductSkeleton.vue';
import getProductPriceBySkuGql from '~/modules/catalog/product/queries/getProductPriceBySku.gql';
import BottomBox from '~/components/ProductDetailPage/BottomBox.vue';

export default defineComponent({
  name: 'ProductPage',
  components: {
    ProductSkeleton,
    SimpleProduct: () => import('~/modules/catalog/product/components/product-types/simple/SimpleProduct.vue'),
    BundleProduct: () => import('~/modules/catalog/product/components/product-types/bundle/BundleProduct.vue'),
    ConfigurableProduct: () => import('~/modules/catalog/product/components/product-types/configurable/ConfigurableProduct.vue'),
    GroupedProduct: () => import('~/modules/catalog/product/components/product-types/grouped/GroupedProduct.vue'),
    VirtualProduct: () => import('~/modules/catalog/product/components/product-types/virtual/VirtualProduct.vue'),
    RelatedProducts: () => import('~/modules/catalog/product/components/RelatedProducts.vue'),
    UpsellProducts: () => import('~/modules/catalog/product/components/UpsellProducts.vue'),
    LoadWhenVisible,
    SfBreadcrumbs,
    SfLoader,
    BottomBox,
  },
  transition: 'fade',
  props: {
    routerDataRes: {
      type: Object,
      default: {}
    },
  },
  setup({ routerDataRes }) {
    const { routeData } = usePageStore();
    const { query } = useApi();
    const product = ref<Product | null>(null);
    const { addTags } = useCache();
    const { localePath } = useContext();
    const route = useRoute();
    const { getProductDetails, loading } = useProduct();
    const { error: nuxtError } = useContext();
    const { load: loadWishlist } = useWishlist();
    /* const { load: loadCompareList } = useComparelist(); */
    const breadcrumbs = computed(() => {
      const productCategories = product.value?.categories ?? [];
      return getBreadcrumbs(
        product.value,
        Array.isArray(productCategories) ? [...productCategories].pop() : null,
      ).map((breadcrumb) => ({ ...breadcrumb, link: breadcrumb.link }));
    });

    /* console.log('breadcrumbs', breadcrumbs); */

    /** .map((breadcrumb) => ({ ...breadcrumb, link: localePath(breadcrumb.link) }))
     *  changed to .map((breadcrumb) => ({ ...breadcrumb, link: breadcrumb.link }));
     */

    const getBaseSearchQuery = () => ({
      filter: {
        sku: {
          eq: (routeData != null && routeData.sku)? routeData.sku : routerDataRes.sku,
        },
      },
      configurations: Object.entries(route.value.query)
        .filter((config) => config[0] !== 'wishlist')
        .map(
          (config) => config[1],
        ) as string[],
    });

    // eslint-disable-next-line no-underscore-dangle
    const renderer = computed(() => product.value?.__typename ?? ProductTypeEnum.SIMPLE_PRODUCT);

    const fetchProductBaseData = async (searchQuery = getBaseSearchQuery()) => {
      const result = await getProductDetails({
        ...searchQuery,
      });
      product.value = merge({}, product.value, result.items[0] as Product ?? null);
    };

    const fetchProductExtendedData = async (searchQuery = getBaseSearchQuery()) => {
      const { data } = await query<ProductDetailsQuery>(getProductPriceBySkuGql, searchQuery);

      if (data.products?.items?.[0]) {
        // Explicit type assertion
        product.value = {
          ...(product.value || {}),
          ...(data.products.items[0] as Product),
        };

      } else {
        console.warn('No product data found.');
      }

      /* product.value = merge({}, product.value, data.products?.items?.[0] as Product); */
    };

    useFetch(async () => {
      await fetchProductBaseData();

      if (Boolean(product?.value?.sku) === false) nuxtError({ statusCode: 404 });

      const tags = [
        {
          prefix: CacheTagPrefix.View,
          value: `product-${(routeData != null && routeData.sku)? routeData.sku : routerDataRes.sku}`,
        },
      ];

      const productTags = [{
        prefix: CacheTagPrefix.Product,
        value: product.value.uid,
      }];

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      addTags([...tags, ...productTags]);
    });

    onMounted(async () => {
      /* var _learnq = _learnq || [];
      var item = {
          "ProductName": product.value.name,
          "ProductID": product.value.id,
          "SKU": product.value.sku,
          "Categories": product.value.categories,
          "ImageURL": product.value.image.url,
          "URL": product.value.url_key + '.html',
          "Brand": product.value.manufacturer_name,
          "Price": product.value.price_range.maximum_price.final_price.value,
      };
      _learnq.push(["track", "Viewed Product", item]);
      console.log('_learnq:- ', _learnq); */
      if(process.client){
        //@ts-ignore
        window.dataLayer = window.dataLayer || [];
        //@ts-ignore
        window.dataLayer.push({
          event: "view_item"
        });
      }      

      await Promise.all([fetchProductExtendedData(), loadWishlist()]); /* ,loadCompareList() */
    });

    return {
      renderer,
      loading,
      breadcrumbs,
      product,
      fetchProduct: fetchProductExtendedData,
    };
  },
  head() {
    return getMetaInfo(this.product);
  },
});
